import React from "react";
import {
  Sankey,
  ResponsiveContainer,
  Layer,
  Rectangle,
  Tooltip,
} from "recharts";

interface SankeyNode {
  name: string;
  percent?: number;
  absoluteValue?: number;
}

interface SankeyLink {
  source: number;
  target: number;
  value: number;
  sourceName?: string;
}

interface SankeyData {
  nodes: SankeyNode[];
  links: SankeyLink[];
}

interface SankeyCardProps {
  title?: string;
  data: SankeyData;
  height?: number;
  className?: string;
  colors?: { [key: string]: string };
}

const CustomTooltip = ({ active, payload }: any) => {
  if (!active || !payload || !payload.length) return null;

  const data = payload[0];

  return (
    <div className="bg-white p-3 shadow-lg rounded-lg border border-gray-100">
      <div className="text-[13px] font-medium text-gray-900 mb-2">
        {data.payload.sourceNode?.name && data.payload.targetNode?.name
          ? `${data.payload.sourceNode.name} → ${data.payload.targetNode.name}`
          : data.name}
      </div>
      <div className="flex items-center justify-between gap-4 text-[13px]">
        <span className="text-gray-500">Count:</span>
        <span className="font-medium text-gray-900">{data.value}</span>
      </div>
      {data.payload.percent && (
        <div className="flex items-center justify-between gap-4 text-[13px]">
          <span className="text-gray-500">Conversion Rate:</span>
          <span className="font-medium text-gray-900">
            {data.payload.percent}%
          </span>
        </div>
      )}
    </div>
  );
};

const SankeyDiagram: React.FC<SankeyCardProps> = ({
  title = "Invitation Flow",
  data,
  height = 192,
  className = "",
  colors = {},
}) => {
  const getDefaultColor = (name: string, index: number) => {
    const defaultColors = [
      "#818cf8",
      "#a78bfa",
      "#60a5fa",
      "#34d399",
      "#f87171",
      "#fb923c",
      "#d4a4eb",
      "#a3e4db",
      "#facc15",
      "#fdba74",
    ];
    return defaultColors[index % defaultColors.length] || "#94a3b8";
  };

  const effectiveColors: { [key: string]: string } = data.nodes.reduce(
    (acc, node, index) => {
      acc[node.name] = colors[node.name] || getDefaultColor(node.name, index);
      return acc;
    },
    {} as { [key: string]: string }
  );

  return (
    <div
      className={`bg-white rounded-lg border border-gray-100 p-4 ${className}`}
    >
      <div className="flex items-center mb-3">
        <h3 className="text-[14px] font-medium text-gray-700">{title}</h3>
      </div>

      <div className="bg-gray-50/50 rounded-lg px-2 py-1" style={{ height }}>
        <ResponsiveContainer width="95%" height="100%">
          <Sankey
            data={data}
            node={({ x, y, width, height, index, payload }) => {
              const name = payload.name;
              const customColor = effectiveColors[name] || "#94a3b8";
              const percent = payload.percent || 0;
              const absoluteValue = payload.absoluteValue || 0;

              const isLeftSide = x < 100;
              const nodeWidth = 20;
              const labelX = isLeftSide ? x - 8 : x + nodeWidth + 8;
              const textAnchor = isLeftSide ? "end" : "start";
              const label = `${name} (${percent}%)`;

              return (
                <Layer key={`sankey-node-${index}`}>
                  <Rectangle
                    x={x}
                    y={y}
                    width={nodeWidth}
                    height={height}
                    fill={customColor}
                    fillOpacity={0.9}
                  />
                  <text
                    x={labelX}
                    y={y + height / 2}
                    textAnchor={textAnchor}
                    dominantBaseline="middle"
                    fill="#4b5563"
                    fontSize={12}
                    fontWeight={500}
                  >
                    {label}
                  </text>
                </Layer>
              );
            }}
            link={({
              sourceX,
              sourceY,
              targetX,
              targetY,
              linkWidth,
              index,
              payload,
            }) => {
              const sourceName = payload.sourceName || "";
              const customColor = effectiveColors[sourceName] || "#94a3b8";

              return (
                <path
                  key={`sankey-link-${index}`}
                  d={`
                    M${sourceX},${sourceY}
                    C${sourceX + (targetX - sourceX) * 0.5},${sourceY}
                     ${sourceX + (targetX - sourceX) * 0.5},${targetY}
                     ${targetX},${targetY}
                  `}
                  fill="none"
                  stroke={customColor}
                  strokeWidth={linkWidth}
                  strokeOpacity={0.3}
                />
              );
            }}
            margin={{ top: 16, right: 120, bottom: 16, left: 90 }}
            nodeWidth={20}
            nodePadding={20}
            iterations={64}
          >
            <Tooltip content={<CustomTooltip />} />
          </Sankey>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default SankeyDiagram;
